import { en } from '#app/locales/en.ts'
import { uk, type TranslationKey } from '#app/locales/uk.ts'
import { useRequestInfo } from './request-info.ts'

export const locales = ['uk', 'en'] as const
export type Locale = (typeof locales)[number]
export const defaultLocale: Locale = 'uk'

const dictionaries: Record<Locale, Record<TranslationKey, string>> = {
	uk,
	en,
}

export function isLocale(value: unknown): value is Locale {
	return locales.includes(value as Locale)
}

/**
 * Translate a key with optional `{placeholder}` interpolation.
 */
export function translate(
	locale: Locale,
	key: TranslationKey,
	values?: Record<string, string | number>,
): string {
	let message = dictionaries[locale][key]
	if (values) {
		for (const [name, value] of Object.entries(values)) {
			message = message.replaceAll(`{${name}}`, String(value))
		}
	}
	return message
}

/**
 * Pick the plural form for `count` using the locale's own plural rules
 * (Ukrainian has one/few/many; English one/other).
 */
export function plural(
	locale: Locale,
	count: number,
	forms: Partial<Record<Intl.LDMLPluralRule, string>> & { other: string },
): string {
	const rule = new Intl.PluralRules(locale).select(count)
	return (forms[rule] ?? forms.other).replaceAll('{count}', String(count))
}

/**
 * A `translate` bound to some locale — the shape shared by server-side
 * `getTranslator(request)` and client-side `useTranslation()`. Schema
 * factories accept it so zod messages resolve in the request locale.
 */
export type Translator = (
	key: TranslationKey,
	values?: Record<string, string | number>,
) => string

export function useLocale(): Locale {
	const requestInfo = useRequestInfo()
	return requestInfo.locale
}

/**
 * Returns a `t` bound to the current request locale:
 * `const t = useTranslation(); t('cabinet.title')`
 */
export function useTranslation() {
	const locale = useLocale()
	const t = (key: TranslationKey, values?: Record<string, string | number>) =>
		translate(locale, key, values)
	return Object.assign(t, { locale })
}

export type { TranslationKey }
